import { ChevronDown, ChevronUp, ExternalLink, Minus, X } from 'lucide-react';
import {
  AnimatePresence,
  motion,
  type Transition,
  useReducedMotion,
} from 'motion/react';
import Link from 'next/link';

import { DownloadManagerRow } from '@/components/download-manager/download-manager-row';
import { DownloadProgressRing } from '@/components/download-manager/download-progress-ring';
import { DownloadStatusIcon } from '@/components/download-manager/download-status-icon';
import { useDownloadManagerDocking } from '@/components/download-manager/use-download-manager-docking';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader } from '@/components/ui/loader';
import { ScrollArea } from '@/components/ui/scroll-area';
import type { DownloadManagerCopy } from '@/i18n/download-manager-copy';
import type { DownloadManagerDockCorner } from '@/lib/download-manager-docking';
import type { DownloadManagerJob } from '@/lib/download-manager-state';
import { cn } from '@/lib/utils';

type DownloadManagerPanelProps = {
  jobs: DownloadManagerJob[];
  copy: DownloadManagerCopy;
  locale: string;
  isExpanded: boolean;
  isCloseDisabled: boolean;
  isClosing: boolean;
  isDeliveryLocked: boolean;
  downloadingJobId: string | null;
  retryingJobId: string | null;
  collapsedJob: DownloadManagerJob;
  downloadsHref: string;
  dockCorner: DownloadManagerDockCorner;
  onClose: () => void;
  onDownload: (job: DownloadManagerJob) => void;
  onRetry: (job: DownloadManagerJob) => void;
  onExpand: () => void;
  onMinimize: () => void;
  onDockCornerChange: (corner: DownloadManagerDockCorner) => void;
};

const DOWNLOAD_MANAGER_EASE = [0.22, 1, 0.36, 1] as const;
export function getDownloadManagerSurfaceRadiusClass(
  isExpanded: boolean
): string {
  return isExpanded ? 'rounded-t-xl md:rounded-[18px]' : 'rounded-[18px]';
}

export function getDownloadManagerDockOriginClass(
  dockCorner: DownloadManagerDockCorner
): string {
  switch (dockCorner) {
    case 'top-left':
      return 'origin-top-left';
    case 'top-right':
      return 'origin-top-right';
    case 'bottom-left':
      return 'origin-bottom-left';
    case 'bottom-right':
      return 'origin-bottom-right';
  }
}

export function getDownloadManagerLayoutTransition(
  prefersReducedMotion: boolean
): Transition {
  return prefersReducedMotion
    ? { layout: { duration: 0 } }
    : {
        layout: {
          duration: 0.4,
          ease: DOWNLOAD_MANAGER_EASE,
        },
      };
}

export function DownloadManagerPanel(props: DownloadManagerPanelProps) {
  const prefersReducedMotion = useReducedMotion() ?? false;
  const contentTransition: Transition = {
    duration: prefersReducedMotion ? 0 : 0.32,
    ease: DOWNLOAD_MANAGER_EASE,
  };
  const layoutTransition =
    getDownloadManagerLayoutTransition(prefersReducedMotion);
  const sharedElementTransition: Transition = prefersReducedMotion
    ? { duration: 0 }
    : { duration: 0.4, ease: DOWNLOAD_MANAGER_EASE };
  const collapsedJobIsActive =
    props.collapsedJob.status === 'queued' ||
    props.collapsedJob.status === 'running';
  const isDockedTop = props.dockCorner.startsWith('top');
  const isDockedRight = props.dockCorner.endsWith('right');
  const {
    boundaryRef,
    panelRef,
    dragControls,
    dragEnabled,
    isReady,
    x,
    y,
    onDragEnd,
    onDragStart,
    onPointerMove,
    onPointerUp,
    shouldSuppressExpand,
    startDrag,
  } = useDownloadManagerDocking({
    dockCorner: props.dockCorner,
    isExpanded: props.isExpanded,
    prefersReducedMotion,
    onDockCornerChange: props.onDockCornerChange,
  });

  return (
    <div
      ref={boundaryRef}
      data-slot="download-manager-boundary"
      className="sidebar-offset-transition pointer-events-none fixed inset-y-0 right-0 left-0 z-50 lg:left-[var(--sidebar-width,0px)]"
    >
      <motion.section
        ref={panelRef}
        layout
        drag={dragEnabled}
        dragConstraints={boundaryRef}
        dragControls={dragControls}
        dragElastic={0}
        dragListener={false}
        dragMomentum={false}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        aria-label={props.copy.title}
        data-dock-corner={props.dockCorner}
        style={{
          x,
          y,
          originX: isDockedRight ? 1 : 0,
          originY: isDockedTop ? 0 : 1,
        }}
        className={cn(
          'border-border/35 bg-background/95 pointer-events-auto absolute flex flex-col overflow-hidden border shadow-2xl backdrop-blur-sm md:inset-auto md:top-0 md:left-0',
          isReady ? 'visible' : 'invisible',
          getDownloadManagerDockOriginClass(props.dockCorner),
          props.isExpanded
            ? 'inset-x-0 bottom-0 w-full md:w-[22rem]'
            : 'inset-x-2 bottom-[max(0.5rem,env(safe-area-inset-bottom))] shadow-[0_4px_20px_-6px_rgba(0,0,0,0.18)] md:w-auto md:min-w-56 dark:shadow-[0_4px_20px_-6px_rgba(0,0,0,0.32)]',
          getDownloadManagerSurfaceRadiusClass(props.isExpanded)
        )}
        transition={layoutTransition}
      >
        <span
          data-slot="download-manager-status-announcement"
          className="sr-only"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          {props.copy.itemCount(props.jobs.length)}
        </span>
        <motion.header
          layout="position"
          data-slot="download-manager-header"
          className={cn(
            'flex h-9 shrink-0 items-center transition-[background-color,border-color] duration-200 ease-out motion-reduce:transition-none',
            props.isExpanded
              ? 'border-border/35 bg-muted/70 dark:bg-muted/30 gap-1 border-b ps-3 pe-1.5'
              : 'gap-2 border-b border-transparent px-1.5'
          )}
          transition={layoutTransition}
        >
          <div
            data-download-manager-drag-handle={
              props.isExpanded ? 'expanded' : 'collapsed'
            }
            onPointerDown={startDrag}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onClick={() => {
              if (!props.isExpanded && !shouldSuppressExpand()) {
                props.onExpand();
              }
            }}
            onKeyDown={(event) => {
              if (
                !props.isExpanded &&
                (event.key === 'Enter' || event.key === ' ')
              ) {
                event.preventDefault();
                props.onExpand();
              }
            }}
            role={props.isExpanded ? undefined : 'button'}
            tabIndex={props.isExpanded ? undefined : 0}
            aria-label={props.isExpanded ? undefined : props.copy.expand}
            className={cn(
              'flex min-w-0 flex-1 items-center md:cursor-grab md:touch-none md:select-none md:active:cursor-grabbing',
              !props.isExpanded && 'pe-6'
            )}
          >
            <motion.div
              initial={false}
              animate={{
                opacity: props.isExpanded ? 0 : 1,
                width: props.isExpanded ? 0 : 32,
              }}
              aria-hidden={props.isExpanded}
              className="flex shrink-0 overflow-hidden"
              transition={sharedElementTransition}
            >
              {collapsedJobIsActive ? (
                <div
                  data-slot="download-manager-job-indicator"
                  className="w-8 shrink-0"
                >
                  <DownloadProgressRing
                    progress={props.collapsedJob.progress}
                    label={props.copy.itemProgress(
                      props.collapsedJob.archiveName,
                      props.collapsedJob.progress
                    )}
                    className="size-6 text-[7px]"
                  />
                </div>
              ) : (
                <span className="flex w-8 shrink-0 items-center">
                  <span
                    data-slot="download-manager-job-indicator"
                    className="flex size-6 items-center justify-center"
                  >
                    <DownloadStatusIcon
                      status={props.collapsedJob.status}
                      className="size-5"
                    />
                  </span>
                </span>
              )}
            </motion.div>
            <h2
              className={cn(
                'min-w-0 truncate',
                props.isExpanded
                  ? 'text-sm font-semibold'
                  : 'text-xs font-medium'
              )}
            >
              <span
                data-slot="download-manager-title"
                className="block truncate"
              >
                {props.copy.title}
              </span>
            </h2>
            <Badge
              variant="secondary"
              className="ms-2 h-5 shrink-0 px-2 text-[10px] tabular-nums"
            >
              {props.copy.itemCount(props.jobs.length)}
            </Badge>
          </div>
          <motion.div
            data-slot="download-manager-external-link-slot"
            initial={false}
            animate={{
              opacity: props.isExpanded ? 1 : 0,
              width: props.isExpanded ? 28 : 0,
            }}
            aria-hidden={!props.isExpanded}
            className={cn(
              'shrink-0 overflow-hidden',
              !props.isExpanded && 'pointer-events-none'
            )}
            transition={sharedElementTransition}
          >
            {props.isExpanded ? (
              <Button
                asChild
                variant="ghost"
                size="icon-sm"
                className="hover:bg-accent/70 size-7 shrink-0 cursor-pointer rounded-full transition-[background-color,border-color,box-shadow,color,opacity,transform] duration-150 ease-out active:scale-[0.96] motion-reduce:transition-none"
              >
                <Link
                  href={props.downloadsHref}
                  aria-label={props.copy.viewAll}
                  title={props.copy.viewAll}
                >
                  <ExternalLink className="size-3.5" />
                </Link>
              </Button>
            ) : null}
          </motion.div>
          <motion.div
            data-slot="download-manager-toggle-slot"
            layout="position"
            transition={layoutTransition}
          >
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              className="hover:bg-accent/70 size-7 cursor-pointer rounded-full transition-[background-color,border-color,box-shadow,color,opacity,transform] duration-150 ease-out enabled:active:scale-[0.96] motion-reduce:transition-none"
              aria-label={
                props.isExpanded ? props.copy.minimize : props.copy.expand
              }
              data-chevron-direction={isDockedTop ? 'down' : 'up'}
              title={props.isExpanded ? props.copy.minimize : props.copy.expand}
              onClick={props.isExpanded ? props.onMinimize : props.onExpand}
            >
              <span
                data-slot="download-manager-toggle-icons"
                className="relative block size-4"
              >
                <motion.span
                  aria-hidden="true"
                  className="absolute inset-0 flex items-center justify-center"
                  initial={false}
                  animate={{
                    opacity: props.isExpanded ? 1 : 0,
                    rotate: props.isExpanded ? 0 : -35,
                    scale: props.isExpanded ? 1 : 0.88,
                  }}
                  transition={contentTransition}
                >
                  <Minus className="size-4" />
                </motion.span>
                <motion.span
                  aria-hidden="true"
                  className="absolute inset-0 flex items-center justify-center"
                  initial={false}
                  animate={{
                    opacity: props.isExpanded ? 0 : 1,
                    rotate: props.isExpanded ? (isDockedTop ? -35 : 35) : 0,
                    scale: props.isExpanded ? 0.88 : 1,
                  }}
                  transition={contentTransition}
                >
                  {isDockedTop ? (
                    <ChevronDown
                      strokeWidth={2.5}
                      className="text-muted-foreground size-4 cursor-pointer"
                    />
                  ) : (
                    <ChevronUp
                      strokeWidth={2.5}
                      className="text-muted-foreground size-4 cursor-pointer"
                    />
                  )}
                </motion.span>
              </span>
            </Button>
          </motion.div>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className={cn(
              'cursor-pointer rounded-full transition-[background-color,color,opacity,transform] duration-150 ease-out enabled:active:scale-[0.96] disabled:pointer-events-auto disabled:cursor-not-allowed motion-reduce:transition-none',
              props.isExpanded ? 'size-7' : 'size-6'
            )}
            disabled={props.isCloseDisabled || props.isClosing}
            aria-label={props.copy.close}
            title={props.copy.close}
            onClick={props.onClose}
          >
            {props.isClosing ? (
              <Loader size="sm" />
            ) : (
              <X className="size-3.5" />
            )}
          </Button>
        </motion.header>

        <AnimatePresence initial={false} mode="popLayout">
          {props.isExpanded ? (
            <motion.div
              key="download-list"
              className="min-h-0 w-full"
              initial={prefersReducedMotion ? false : { opacity: 0, y: -3 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 1, y: 0 }}
              transition={
                props.isExpanded ? contentTransition : layoutTransition
              }
            >
              <ScrollArea
                className="max-h-[13rem] min-h-0"
                viewportClassName="max-h-[13rem] overflow-x-hidden [&>div]:!block [&>div]:!min-w-0 [&>div]:!w-full"
                maskHeight={12}
                showHorizontalScrollbar={false}
              >
                <div className="divide-border/35 divide-y">
                  {props.jobs.map((job) => (
                    <DownloadManagerRow
                      key={job.id}
                      job={job}
                      copy={props.copy}
                      locale={props.locale}
                      isDeliveryLocked={props.isDeliveryLocked}
                      isDownloading={props.downloadingJobId === job.id}
                      isRetryLocked={props.retryingJobId !== null}
                      isRetrying={props.retryingJobId === job.id}
                      onDownload={props.onDownload}
                      onRetry={props.onRetry}
                    />
                  ))}
                </div>
              </ScrollArea>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </motion.section>
    </div>
  );
}
